import { useEffect, useState } from "react";

import { debounce } from "lodash";

import {
  FETCH_TIK_TOK_SHOP_ORDER_TAG_INCLUSIONS,
  UPDATE_SHOP_ORDER_TAG_INCLUSIONS,
} from "@gql/shops-order-tag-inclusions";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type UpdateShopOrderTagInclusions,
  type UpdateShopOrderTagInclusionsVariables,
  type FetchTikTokShopOrderTagInclusions,
} from "@north-beam/nb-client-side";
import { type TagBasedOrderExclusion } from "@north-beam/nb-common";

export const useTikTokShopTagInclusions = () => {
  const [isSynced, setIsSynced] = useState<boolean>(true);

  const [tikTokShopOrderTagInclusions, setTikTokShopOrderTagInclusions] =
    useState<TagBasedOrderExclusion[]>([]);

  const { data, loading } =
    useNorthbeamQuery<FetchTikTokShopOrderTagInclusions>(
      FETCH_TIK_TOK_SHOP_ORDER_TAG_INCLUSIONS,
    );

  const [updateTikTokShopOrderInclusions] = useNorthbeamMutation<
    UpdateShopOrderTagInclusions,
    UpdateShopOrderTagInclusionsVariables
  >(UPDATE_SHOP_ORDER_TAG_INCLUSIONS);

  useEffect(() => {
    if (data?.me?.tikTokShopOrderTagInclusions) {
      setTikTokShopOrderTagInclusions((inclusions) => {
        if (inclusions.length === 0) {
          return data.me.tikTokShopOrderTagInclusions;
        }
        return inclusions;
      });
    }
  }, [data?.me?.tikTokShopOrderTagInclusions]);

  const debouncedUpdate = debounce(
    async (newData: TagBasedOrderExclusion[]) => {
      setIsSynced(false);
      await updateTikTokShopOrderInclusions({
        variables: { shop: "tiktok", shopOrderTagInclusions: newData },
        refetchQueries: [{ query: FETCH_TIK_TOK_SHOP_ORDER_TAG_INCLUSIONS }],
      });
      setIsSynced(true);
    },
    250,
    { leading: false, trailing: true },
  );

  const updateOrderTagByIndex = (
    index: number,
    newData: TagBasedOrderExclusion,
  ) => {
    const copyInclusions = [...tikTokShopOrderTagInclusions];
    copyInclusions[index] = newData;
    setTikTokShopOrderTagInclusions(copyInclusions);
    debouncedUpdate(copyInclusions);
  };

  const createOrderTagInclusion = () => {
    const newExclusion: TagBasedOrderExclusion = {
      name: "",
      tagType: "order",
      pattern: "",
      tagLogic: "is",
      active: false,
    };
    const copyInclusions = [...tikTokShopOrderTagInclusions, newExclusion];
    setTikTokShopOrderTagInclusions(copyInclusions);
    debouncedUpdate(copyInclusions);
  };

  const removeOrderTagInclusion = (index: number) => {
    const copyInclusions = [...tikTokShopOrderTagInclusions];
    copyInclusions.splice(index, 1);
    setTikTokShopOrderTagInclusions(copyInclusions);
    debouncedUpdate(copyInclusions);
  };

  return {
    isSynced,
    loading,
    tikTokShopOrderTagInclusions,
    updateOrderTagByIndex,
    createOrderTagInclusion,
    removeOrderTagInclusion,
  };
};
